/**
 * Changing one of these on the header is a deliberate act of recoding the whole
 * invoice, so it pushes down to every line — including lines that already carry
 * a value. Overriding a single line afterwards is how the user opts out of the
 * header value (XPEN-3195).
 *
 * The backend cascade (`utils.update_list_item`) stays fill-if-empty, so it has
 * nothing left to do on save and the two never disagree.
 */

// Matches `isBillableLine` in additional-fields-modal.js.
const isBillableLine = (item) => item.IsBillable === true || item.IsBillable === 1;

// GLSection has no picker on a billable line (additional-fields-modal.js:590),
// so cascading it there would persist a value the user can neither see nor
// remove. GLDepartement renders on every line.
const CASCADE_FIELDS = {
  GLSection: (item) => !isBillableLine(item),
  GLDepartement: () => true,
};

// Field → per-line hidden-field id; must match the map in additional-fields-modal.js.
const LINE_HIDDEN_FIELD_ID = {
  GLSection: 'LineGLSection',
  GLDepartement: 'LineGLDepartement',
};

const isFieldHiddenOnLines = (field, hiddenFieldIds) => {
  const id = LINE_HIDDEN_FIELD_ID[field];
  return !!id && (hiddenFieldIds || []).includes(id);
};

// GLSection is stored as a {code, name} option; a cleared select leaves an
// object with no code behind, which is empty in every sense that matters here.
const isBlank = (value) => {
  if (value === undefined || value === null || value === '') return true;
  if (typeof value === 'object') return !value.code;
  return false;
};

const copyValue = (value) => (typeof value === 'object' ? { ...value } : value);

// No `<field>Status` is written: the line inherited this from the header rather
// than being edited, and 4 (the editor's user-set marker) would misreport that.
export function cascadeHeaderFieldToLines(json, field, hiddenFieldIds = []) {
  const appliesTo = CASCADE_FIELDS[field];
  if (!json || !appliesTo) return;
  if (isFieldHiddenOnLines(field, hiddenFieldIds)) return;

  // Clearing the header leaves the lines alone rather than wiping their coding.
  const value = json[field];
  if (isBlank(value)) return;

  (json.ListItem || []).forEach((item) => {
    if (!item || !appliesTo(item)) return;
    item[field] = copyValue(value);
  });
}

/**
 * A line added after the header was coded never sees `cascadeHeaderFieldToLines`
 * — adding a line doesn't route through the header branch of `updateInvoice` —
 * so it would stay blank on screen until the invoice was reopened, which is the
 * symptom this module exists to remove. Fill-if-empty: the caller may already
 * have coded the new line.
 */
export function applyHeaderFieldsToNewLine(json, item, hiddenFieldIds = []) {
  if (!json || !item) return item;

  Object.entries(CASCADE_FIELDS).forEach(([field, appliesTo]) => {
    if (!appliesTo(item) || isFieldHiddenOnLines(field, hiddenFieldIds) || !isBlank(item[field])) return;
    const value = json[field];
    if (isBlank(value)) return;
    item[field] = copyValue(value);
  });

  return item;
}
